// src/pages/PlayerProfilePage.jsx
import { useParams, Link, useNavigate } from "react-router-dom";
import { FaTiktok, FaTwitch, FaYoutube, FaTwitter, FaInstagram, FaArrowLeft, FaTrophy, FaGamepad, FaCalendar, FaMapMarkerAlt } from "react-icons/fa";
import { usePlayers } from "../hooks/usePlayers";
import PlayerPhoto from "../components/PlayerPhoto";



function PlayerProfilePage() {
  const { playerId } = useParams();
  const navigate = useNavigate();
  const { players, loading } = usePlayers();

  const player = players.find(p => p.id.toLowerCase() === playerId.toLowerCase());

  // Get other players in the same division for "Team" section
  const teammates = player
    ? players.filter(p => p.division === player.division && p.id !== player.id).slice(0, 4)
    : [];

  // Don't flash "not found" while the roster is still arriving
  if (loading) {
    return (
      <main className="bg-black min-h-screen flex items-center justify-center">
        <p className="text-gray-500 animate-pulse">Loading…</p>
      </main>
    );
  }

  if (!player) {
    return (
      <main className="bg-black min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-4xl font-bold text-white mb-4">Player Not Found</h2>
          <p className="text-gray-400 mb-8">The player you're looking for doesn't exist.</p>
          <Link
            to="/roster"
            className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wide rounded transition-colors"
          >
            <FaArrowLeft />
            Back to Roster
          </Link>
        </div>
      </main>
    );
  }

  const socialIcons = {
    tiktok: { icon: FaTiktok, color: "hover:text-pink-400", label: "TikTok" },
    twitch: { icon: FaTwitch, color: "hover:text-purple-400", label: "Twitch" },
    youtube: { icon: FaYoutube, color: "hover:text-red-500", label: "YouTube" },
    twitter: { icon: FaTwitter, color: "hover:text-blue-400", label: "Twitter" },
    instagram: { icon: FaInstagram, color: "hover:text-pink-500", label: "Instagram" },
  };

  return (
    <main className="bg-black min-h-screen">
      {/* Hero Section with Player Image */}
      <section className="relative min-h-[70vh] flex items-end">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          {/* Keyed by player so the failed/loaded state can never carry over
              when navigating from one profile to another. */}
          <PlayerPhoto
            key={player.id}
            player={player}
            eager
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-transparent"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-6xl mx-auto px-6 pb-16 w-full">
          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="mb-8 inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <FaArrowLeft />
            <span className="uppercase text-sm font-semibold tracking-wide">Back</span>
          </button>

          {/* Player Info */}
          <div className="mb-4">
            <span className="inline-block px-4 py-1 bg-red-600 text-white text-sm font-bold uppercase tracking-wide rounded">
              {player.division}
            </span>
          </div>
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-black uppercase tracking-tight text-white mb-4">
            {player.name}
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-2xl">
            {player.specialization}
          </p>

          {/* Social Links */}
          {player.socials && Object.keys(player.socials).length > 0 && (
            <div className="flex gap-4 mt-8">
              {Object.entries(player.socials).map(([platform, url]) => {
                if (!url) return null;
                const social = socialIcons[platform];
                if (!social) return null;
                return (
                  <a
                    key={platform}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`p-3 bg-gray-900/80 rounded-lg text-white ${social.color} transition-colors backdrop-blur-sm`}
                    aria-label={social.label}
                  >
                    <social.icon size={24} />
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Player Details */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-12">
              {/* Bio */}
              {player.bio && (
                <div>
                  <h2 className="text-2xl font-bold text-white mb-6 uppercase border-l-4 border-red-600 pl-4">
                    About
                  </h2>
                  <p className="text-gray-300 text-lg leading-relaxed">
                    {player.bio}
                  </p>
                </div>
              )}

              {/* Achievements */}
              {player.achievements && player.achievements.length > 0 && player.achievements[0] !== "Pending" && player.achievements[0] !== "Upcoming" && (
                <div>
                  <h2 className="text-2xl font-bold text-white mb-6 uppercase border-l-4 border-red-600 pl-4">
                    Achievements
                  </h2>
                  <div className="space-y-4">
                    {player.achievements.map((achievement, index) => (
                      <div
                        key={index}
                        className="flex items-start gap-4 p-4 bg-gray-900 rounded-lg border border-gray-800"
                      >
                        <FaTrophy className="text-red-600 text-xl flex-shrink-0 mt-1" />
                        <span className="text-gray-300">{achievement}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Quick Info Card */}
              <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
                <h3 className="text-lg font-bold text-white mb-4 uppercase">
                  Player Info
                </h3>
                <div className="space-y-4">
                  {player.country && player.country !== "Pending" && (
                    <div className="flex items-center gap-3">
                      <FaMapMarkerAlt className="text-red-600" />
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Country</p>
                        <p className="text-white">{player.country}</p>
                      </div>
                    </div>
                  )}
                  {player.age && player.age !== "Pending" && (
                    <div className="flex items-center gap-3">
                      <FaGamepad className="text-red-600" />
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Age</p>
                        <p className="text-white">{player.age}</p>
                      </div>
                    </div>
                  )}
                  {player.joined && player.joined !== "Pending" && (
                    <div className="flex items-center gap-3">
                      <FaCalendar className="text-red-600" />
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Joined</p>
                        <p className="text-white">{player.joined}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Division Card */}
              <div className="bg-gradient-to-br from-red-900/30 to-gray-900 rounded-lg p-6 border border-red-900/30">
                <h3 className="text-lg font-bold text-white mb-2 uppercase">
                  Division
                </h3>
                <p className="text-red-500 font-semibold">{player.division}</p>
                <p className="text-gray-400 text-sm mt-2">{player.specialization}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Teammates Section */}
      {teammates.length > 0 && (
        <section className="py-16 px-6 bg-gray-950">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-8 uppercase border-l-4 border-red-600 pl-4">
              Team Members
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {teammates.map((teammate) => (
                <Link
                  key={teammate.id}
                  to={`/roster/${teammate.id}`}
                  className="group relative bg-gray-900 rounded-lg overflow-hidden border border-gray-800 hover:border-red-600/50 transition-all duration-300"
                >
                  <div className="aspect-square overflow-hidden bg-gray-800">
                    <PlayerPhoto
                      key={teammate.id}
                      player={teammate}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-60 group-hover:opacity-80 transition-opacity"></div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-lg font-bold text-white group-hover:text-red-500 transition-colors">
                      {teammate.name}
                    </h3>
                    <p className="text-sm text-gray-400">{teammate.country}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Call to Action */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Want to See More?
          </h2>
          <p className="text-gray-400 mb-8">
            Check out our full roster or follow {player.name} on social media.
          </p>
          <Link
            to="/roster"
            className="inline-flex items-center gap-2 px-8 py-4 bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wide rounded transition-colors"
          >
            View Full Roster
          </Link>
        </div>
      </section>
    </main>
  );
}

export default PlayerProfilePage;
